import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { mediaUrl } from "@/lib/api";
import { Download, MapPin, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Reel() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/reel/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setErr(true));
  }, [id]);

  if (err)
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500" data-testid="reel-notfound">
        This reel isn’t available.
      </div>
    );
  if (!data)
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );

  const src = mediaUrl(data.final_media_id);
  const vertical = data.aspect === "9:16";
  const chips = (data.chips && data.chips.length)
    ? data.chips
    : [data.beds && `${data.beds} Bed`, data.baths && `${data.baths} Bath`, data.sqft && `${data.sqft} sqft`].filter(Boolean);

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="reel-page">
      <div className="max-w-4xl mx-auto px-5 py-10">
        <div className="mb-6">
          {data.price ? <div className="inline-block bg-amber-500 text-zinc-950 font-extrabold text-2xl px-4 py-1.5 rounded-sm mb-3">{data.price}</div> : null}
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight">{data.title}</h1>
          {data.address ? (
            <p className="mt-2 text-zinc-400 flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-500" /> {data.address}</p>
          ) : null}
          {chips.length ? (
            <div className="mt-3 flex gap-2 flex-wrap">
              {chips.map((c) => (
                <span key={c} className="text-sm text-zinc-200 px-3 py-1 rounded-sm border border-zinc-700">{c}</span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex justify-center bg-black border border-zinc-800 rounded-lg p-4">
          <video data-testid="reel-video" src={src} controls autoPlay playsInline
            className={`rounded-md ${vertical ? "max-h-[75vh]" : "w-full"}`} />
        </div>

        <div className="mt-5 flex justify-center">
          <a href={src} download>
            <button className="h-11 px-6 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors flex items-center">
              <Download className="w-4 h-4 mr-2" /> Download Video
            </button>
          </a>
        </div>

        {data.social && data.social.caption ? (
          <div className="mt-8 border border-zinc-800 bg-zinc-900/40 rounded-lg p-5">
            <p className="text-zinc-200 whitespace-pre-line">{data.social.caption}</p>
            {data.social.hashtags?.length ? (
              <p className="mt-3 text-sky-400/90 text-sm">{data.social.hashtags.join(" ")}</p>
            ) : null}
          </div>
        ) : null}

        <p className="mt-10 text-center text-xs text-zinc-600">Made with an AI reel studio</p>
      </div>
    </div>
  );
}
